import type { FC } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import { ReviewScreenLayout } from "@/pages/postloss/modules/components/ReviewScreenLayout";
import { useInsuranceReviewRedirect } from "@/pages/postloss/modules/hooks/useInsuranceReviewRedirect";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import {
  LifeInsuranceForm,
  parseItemToForm,
  buildMetadata,
  type LifeInsuranceFormValues,
} from "./LifeInsuranceForm";
import { buildInsuranceSubtitle } from "./insuranceSubtitles";
import { useUploadSupportingDocument } from "@/shared/hooks/useUploadSupportingDocument";

export const LifeInsuranceReview: FC<FlowCustomComponentProps> = ({
  onComplete,
  goToScreen,
  setAnswer,
  answers,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, isLoading, isFetching, updateItem, isUpdatingItem, deleteItem, itemActions } =
    useEstateItemActions(estateId);
  const { upload } = useUploadSupportingDocument();

  const firstName = person?.first_name ?? "their";
  const policies = filterByCategory(assets, "Life insurance");
  const hasExtracted = policies.some((p) => p.extracted_from_document);

  const { willRedirect } = useInsuranceReviewRedirect({
    items: policies,
    isLoading,
    isFetching,
    answers,
    setAnswer,
    goToScreen,
    reviewKey: "life_insurance_review",
    manageKey: "life_insurance_manage",
    manageScreen: "life_insurance_manage",
    fallbackScreen: "has_life_insurance",
  });

  return (
    <ReviewScreenLayout<LifeInsuranceFormValues>
      items={policies}
      isLoading={isLoading}
      isFetching={isFetching}
      isRedirecting={willRedirect}
      screenTitle={
        hasExtracted
          ? "We found these life insurance policies in your documents"
          : `Review ${firstName}'s life insurance policies`
      }
      screenSubtitle="Review the details and add any others we might have missed."
      itemLabel="life insurance policies"
      listTitle="Life Insurance Policies"
      addButtonLabel="Add policy"
      editDialogTitle="Edit policy"
      deleteDialogTitle="Delete Policy"
      renderSubtitle={buildInsuranceSubtitle}
      isUpdatingItem={isUpdatingItem}
      deleteItem={deleteItem}
      itemActions={itemActions}
      onContinue={() => onComplete()}
      onAddAnother={() =>
        goToScreen("add_life_insurance", {
          ...answers,
          has_life_insurance: "yes",
          add_life_insurance: "yes",
          life_insurance_manage: "yes",
        })
      }
      parseItemToForm={(item) =>
        parseItemToForm(
          item.item_name,
          item.item_type ?? "",
          item.balance,
          item.notes,
          item.metadata,
        )
      }
      renderEditForm={({ form, onChange, existingFileName, onFileRemove }) => (
        <LifeInsuranceForm
          form={form}
          onChange={onChange}
          existingDocumentFileName={existingFileName}
          onDocumentRemoved={onFileRemove}
        />
      )}
      onSaveEdit={async ({ id, form, fileRemoved }) => {
        if (!estateId) return;

        if (!form.companyName.trim()) {
          throw new Error("Please enter the insurance company name");
        }

        let sourceDocumentId: number | undefined;

        if (form.documentFile) {
          sourceDocumentId = await upload(form.documentFile, estateId, "life_insurance_policy");
        }

        const sourceDocUpdate = sourceDocumentId
          ? { source_document_id: sourceDocumentId }
          : fileRemoved
            ? { source_document_id: null }
            : {};

        await updateItem(id, {
          item_name: form.companyName.trim(),
          item_type: form.policyType || undefined,
          balance: parseFloat(form.coverageAmount) || 0,
          metadata: buildMetadata(form),
          ...sourceDocUpdate,
        });
      }}
    />
  );
};
